import { AbstractAdapter } from './abstract_adapter.js'
import { TranscriptionEvents } from '../events.js'

let ephemeralKey = null
let ephemeralKeyExpiresAt = null
export async function getEphemeralKey() {
  try {
    if (ephemeralKey && ephemeralKeyExpiresAt) {
      const oneMinuteBeforeExpiry = new Date(ephemeralKeyExpiresAt.getTime() - 1 * 60 * 1000)
      // We have more than a minute remaining... Use the key.
      if (oneMinuteBeforeExpiry > new Date()) {
        return ephemeralKey
      }
    }

    // Dont have a key or it's expired. Get a new one.
    const response = await fetch('/voice_transcription/ephemeral_key', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const data = await response.json()

    ephemeralKey = data.ephemeral_key
    ephemeralKeyExpiresAt = new Date(data.expires_at)
    return ephemeralKey
  } catch (error) {
    throw new Error(`Failed to get ephemeral key: ${error.message}`)
  }
  // ------
}

/**
 * OpenAIAdapter - OpenAI Realtime API implementation using WebRTC
 */
export class OpenAIAdapter extends AbstractAdapter {
  constructor(config) {
    super(config)
    this.peerConnection = null
    this.dataChannel = null
    this.keyExpiresAt = null

    // WebRTC configuration
    this.rtcConfiguration = {
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    }

    const params = new URLSearchParams(window.location.search)
    // Session configuration for OpenAI
    this.sessionConfig = {
      input_audio_format: 'pcm16',
      input_audio_transcription: {
        model: params.get('voice_model') || 'gpt-4o-transcribe',
        // model: 'whisper-1',
        language: params.get('voice_lang') == 'dynamic' ? undefined : params.get('voice_lang') || 'en',
      },
      turn_detection: {
        type: 'server_vad',
        // threshold: 0.5,
        // prefix_padding_ms: 300,
        // silence_duration_ms: 200,
        // type: 'semantic_vad',
        // eagerness: 'high',
      },
    }

    // Bind methods
    this.handleDataChannelMessage = this.handleDataChannelMessage.bind(this)
    this.handlePeerConnectionStateChange = this.handlePeerConnectionStateChange.bind(this)
  }

  async connect(getMediaStream) {
    this.setConnectionState('connecting')

    try {
      // Initialize WebRTC peer connection
      await this.initializePeerConnection()

      // Get and add media stream
      const mediaStream = await getMediaStream()
      await this.addAudioTrack(mediaStream)

      // Setup session configuration
      await this.setupSessionConfiguration()

      // Create offer and handle answer
      await this.createOffer()
    } catch (error) {
      this.setConnectionState('error')
      throw new Error(`Failed to connect to OpenAI: ${error.message}`)
    }
  }

  async disconnect() {
    this.setConnectionState('disconnected')

    // Close data channel
    if (this.dataChannel) {
      this.dataChannel.close()
      this.dataChannel = null
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }
  }

  async addAudioTrack(mediaStream) {
    if (!mediaStream || !mediaStream.getAudioTracks().length) {
      throw new Error('No audio tracks found in media stream')
    }

    const audioTrack = mediaStream.getAudioTracks()[0]
    this.peerConnection.addTrack(audioTrack, mediaStream)
  }

  // WebRTC implementation methods

  async initializePeerConnection() {
    this.peerConnection = new RTCPeerConnection(this.rtcConfiguration)

    // Set up event handlers
    this.peerConnection.onconnectionstatechange = this.handlePeerConnectionStateChange
    this.peerConnection.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', this.peerConnection.iceConnectionState)
    }

    // Create data channel for sending events/audio
    this.dataChannel = this.peerConnection.createDataChannel('oai-events', {
      ordered: true,
    })

    this.dataChannel.onopen = () => {
      this.setConnectionState('connected')
    }

    this.dataChannel.onmessage = this.handleDataChannelMessage
    this.dataChannel.onerror = (error) => {
      this.handleError(`Data channel error: ${error}`)
    }
  }

  async setupSessionConfiguration() {
    // Send session configuration once data channel is open
    this.dataChannel.addEventListener(
      'open',
      () => {
        this.sendEvent('transcription_session.update', {
          session: this.sessionConfig,
        })
      },
      { once: true }
    )
  }

  async createOffer() {
    try {
      // Create SDP offer
      const offer = await this.peerConnection.createOffer()
      await this.peerConnection.setLocalDescription(offer)
      const key = await getEphemeralKey()

      // Send offer to OpenAI Realtime API
      const response = await fetch('https://api.openai.com/v1/realtime', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${key}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      })

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.status}`)
      }

      // Get SDP answer
      const answerSdp = await response.text()
      const answer = {
        type: 'answer',
        sdp: answerSdp,
      }

      await this.peerConnection.setRemoteDescription(answer)
    } catch (error) {
      throw new Error(`Failed to create WebRTC offer: ${error.message}`)
    }
  }

  handleDataChannelMessage(event) {
    try {
      const message = JSON.parse(event.data)
      this.handleRealtimeEvent(message)
    } catch (error) {
      console.error('Error parsing data channel message:', error)
    }
  }

  handleRealtimeEvent(event) {
    switch (event.type) {
      case 'transcription_session.created':
        this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_STARTED)
        break

      case 'input_audio_buffer.speech_started':
        this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_PROCESSING)
        break

      case 'conversation.item.input_audio_transcription.completed':
        this.insertSpace = true
        this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_PROCESSED)
        break

      case 'conversation.item.input_audio_transcription.delta':
        const text = `${this.insertSpace ? ' ' : ''}${event.delta}`
        this.insertSpace = false
        this.currentTranscription = this.currentTranscription || ''
        this.currentTranscription += event.delta || ''
        this.emit(TranscriptionEvents.EVENTS.TRANSCRIPTION_UPDATE, {
          text,
          isFinal: false,
          confidence: 0.8,
        })
        break

      case 'error':
        this.handleError(`OpenAI API error: ${event.error.message}`)
        break

      default:
        console.log('Unhandled OpenAI event:', event.type)
    }
  }

  handlePeerConnectionStateChange() {
    const state = this.peerConnection.connectionState

    if (state === 'connected') {
      this.setConnectionState('connected')
    } else if (state === 'disconnected' || state === 'failed') {
      this.setConnectionState('error')
      this.handleError('WebRTC connection failed')
    }
  }

  sendEvent(eventType, data = {}) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      console.warn('Cannot send event: data channel not open')
      return
    }

    const event = {
      type: eventType,
      ...data,
    }

    this.dataChannel.send(JSON.stringify(event))
  }
}